import type * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as common from '../../../common'

import * as gql from './gql'
import type { TypeParams } from './types'

const NON_NULL_AGENT_ID_TRANSFORMER = graphql.transformers.IN.nonNull(
  common.gql.enums.agentId.TRANSFORMER,
)

/**
 * API for managing assistant (agent) conversations
 *
 * @see {@link ec.models.Api}
 */
export class Api implements ec.models.Api<TypeParams> {
  /** @inheritdoc */
  public list = ec.models.Api.list.using.onlySort<TypeParams>(
    (context, request) =>
      context
        ? rxjs
            .forkJoin(
              this._agentIds.map(agentId =>
                gql.queries.listConversations.executor({
                  agentId: NON_NULL_AGENT_ID_TRANSFORMER.getGqlFrom(agentId),
                  tenantId: context.tenantId,
                  zoneId: context.zoneId,
                }),
              ),
            )
            .pipe(
              rxjs.map(conversationaLists => conversationaLists.flat()),
              rxjs.map(conversations =>
                [...conversations].sort((a, b) => {
                  if (!request.sort) {
                    return 0
                  }

                  const prop = request.sort
                    .by as keyof (typeof conversations)[0]
                  const [first, second] = request.sort.descending
                    ? [b, a]
                    : [a, b]
                  const firstVal = first[prop]
                  const secondVal = second[prop]

                  if (firstVal instanceof Date && secondVal instanceof Date) {
                    return firstVal.getTime() - secondVal.getTime()
                  }

                  if (
                    typeof firstVal === 'string' &&
                    typeof secondVal === 'string'
                  ) {
                    return firstVal.localeCompare(secondVal)
                  }

                  if (
                    typeof firstVal === 'number' &&
                    typeof secondVal === 'number'
                  ) {
                    return firstVal - secondVal
                  }

                  if (
                    typeof firstVal === 'boolean' &&
                    typeof secondVal === 'boolean'
                  ) {
                    return (firstVal ? 1 : 0) - (secondVal ? 1 : 0)
                  }

                  throw new Error(
                    `Cannot sort by property '${String(
                      prop,
                    )}' of type '${typeof firstVal}'`,
                  )
                }),
              ),
            )
        : rxjs.of([]),
  )

  constructor(
    private readonly _agentIds: readonly ents.core.agent.Agent.Id[],
  ) {}

  /** @inheritdoc */
  public onChange: ec.models.Api<TypeParams>['onChange'] = context => {
    if (!context) {
      return rxjs.EMPTY
    }

    return rxjs.merge(
      ...this._agentIds.map(agentId =>
        gql.subscription.onAgentConversationPublished.executor({
          agentId: NON_NULL_AGENT_ID_TRANSFORMER.getGqlFrom(agentId),
          tenantId: context.tenantId,
          zoneId: context.zoneId,
        }),
      ),
    )
  }

  /** @inheritdoc */
  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    if (!context) {
      return rxjs.EMPTY
    }

    return gql.mutations.deleteConversation.executor({
      conversationId: id,
      tenantId: context.tenantId,
      zoneId: context.zoneId,
    })
  }

  /** @inheritdoc */
  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id, updates },
  ) => {
    if (!context) {
      return rxjs.EMPTY
    }

    TypeAsserter.assertIsNotNullish(updates.agentId)
    return gql.mutations.putConversation.executor({
      input: {
        agentId: NON_NULL_AGENT_ID_TRANSFORMER.getGqlFrom(updates.agentId),
        id,
        name: updates.name,
        sharedWith: updates.sharedWith,
        workspaceId: updates.workspaceId,
      },
      tenantId: context.tenantId,
      zoneId: context.zoneId,
    })
  }
}
